import { z } from 'zod'
import { EXCHANGE_UNDO_MINUTES, exchangeDaySchema } from '#model/entities/exchange'
import { convertAcross } from '#model/entities/trip'
import { ERROR, ISSUE } from '#model/support/errors'
import { visibleLine } from '#model/support/text'
import { priceSchema } from '#model/values/money'
import type { Currency, Money } from '#model/values/money'
import { exchangeRateSchema } from '#model/values/rates'

/** «Что это» and «Где»: free text of one line, short enough for a row of the journal (handoff 02). */
export const SPENDING_TEXT_MAX = 80
export const spendingTextSchema = visibleLine(SPENDING_TEXT_MAX)

/** The same ten minutes as an exchange and an income (MOL-73, В-4): one rule for one's own money. */
export const SPENDING_UNDO_MINUTES = EXCHANGE_UNDO_MINUTES

const positiveMoneySchema = priceSchema.refine((value) => value.minor > 0n, {
  error: ERROR.INVALID_AMOUNT,
})

/**
 * Money spent outside a trip (MOL-73): the barber, the rent, the domain — whatever the catalogue of
 * goods does not hold. A day, an amount in its own currency, one of the person's categories, and
 * two optional lines of their own words. It makes no item, feeds no price and no verdict (В-2): a
 * purchase at a shop is still entered in «Поход», and the sheet says so.
 *
 * `rate` is the snapshot a spending in another currency than the spending one is counted by — the
 * rate of **its own day**, taken when it was written and never recomputed (CLAUDE.md, «Money»):
 * between the spending's own currency and the spending currency of that moment, on whichever side
 * its number is at least one — «390 ֏ за $» for a dollar spending, «4,1 ֏ за ₽» for a rouble one,
 * and for someone who counts in dollars a dram spending too is «390 ֏ за $». Null when the two are
 * one currency, and when nothing was known that day — then the month counts it the way it counts a
 * trip, by the rule of its day as known now (review Р-5).
 */
export const spendingSchema = z
  .object({
    id: z.uuid(),
    actorId: z.uuid(),
    spentOn: exchangeDaySchema,
    amount: positiveMoneySchema,
    categoryId: z.uuid(),
    note: spendingTextSchema.nullable(),
    place: spendingTextSchema.nullable(),
    rate: exchangeRateSchema.nullable(),
    /** The account it was paid from, or null — it then moves no balance (MOL-115, В-2). */
    accountId: z.uuid().nullable(),
    /**
     * «Списано со счёта» (MOL-43 В-3): what left the account exactly, in its currency, when the
     * spending's is another. It moves the balance and nothing else — not the month (MOL-115 В-1),
     * not the person's own rate: those drams were never in their hands.
     */
    debited: positiveMoneySchema.nullable(),
    /**
     * The transfer this spending is the fee of (MOL-253, Р-1): written, amended and removed with it,
     * never through «Траты» on its own. Null for every spending the person wrote.
     */
    transferId: z.uuid().nullable(),
    revision: z.int().min(1),
    createdAt: z.date(),
    amendedAt: z.date().nullable(),
  })
  .refine(
    ({ rate, amount }) =>
      rate === null || rate.base === amount.currency || rate.quote === amount.currency,
    {
      error: ISSUE.RATE_NOT_OF_SPENDING_CURRENCY,
    },
  )
  .refine(({ accountId, debited }) => debited === null || accountId !== null, {
    error: ISSUE.DEBITED_WITHOUT_ACCOUNT,
  })
export type Spending = z.infer<typeof spendingSchema>

/**
 * The spending in `currency` — the month's spending currency: as it is when it is already in it, by
 * its own day's snapshot when that snapshot is of this pair, and null when neither — a snapshot into
 * drams says nothing of dollars after a move, and the month then counts the spending as it counts a
 * trip line (review Р-5, adversarial Д8). Null too when it comes to more than money holds.
 */
export function spendingIn(
  spending: Pick<Spending, 'amount' | 'rate'>,
  currency: Currency,
): Money | null {
  const { amount, rate } = spending
  if (amount.currency === currency) return amount
  if (rate === null || (rate.base !== currency && rate.quote !== currency)) return null
  return convertAcross(amount, rate)
}
